import React, { useState, useEffect } from "react";
import api from "../utils/axiosInstance";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useModal } from "../context/ModalContext";
import { downloadSampleCsv } from "../utils/csvSamples";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaFileExcel,
  FaSearch,
  FaUpload,
  FaWhatsapp,
  FaHistory,
  FaTrash,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import {
  Card, CardHeader, CardBody,
  PageHeader, Button, Spinner,
  TableWrapper, Table, Th, Td,
  EmptyState, Input, InputGroup, Select,
} from "../components/ui";

const HISTORY_PAGE_SIZE = 200;

export default function VerifyNumbers() {
  const modal = useModal();

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  // Single verify
  const [singleNumber, setSingleNumber] = useState("");
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);

  // Bulk verify
  const [file, setFile] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // History log
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Format guide
  const [showGuide, setShowGuide] = useState(false);

  const fetchHistory = async (sid, page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/api/verify/history", {
        params: { session: sid || undefined, page, limit: HISTORY_PAGE_SIZE },
      });
      // Tolerate both the legacy array response and the new paginated object.
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : (payload?.records || []);
      setHistory(list);
      setHistoryPage(payload?.page || page);
      setHistoryPages(payload?.pages || 1);
      setHistoryTotal(typeof payload?.total === "number" ? payload.total : list.length);
    } catch {
      setHistory([]);
      setHistoryPage(1);
      setHistoryPages(1);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) fetchHistory(sessionId, 1);
  }, [sessionId]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await api.get("/api/sessions");
        const data = res?.data?.data ?? res?.data ?? [];
        const list = Array.isArray(data) ? data : [];
        const connected = list.filter((s) => s.status === "connected");
        setSessions(connected);
        if (connected.length > 0) {
          setSessionId(connected[0].sessionId);
        }
      } catch {
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const handleSingleVerify = async () => {
    if (!singleNumber || !sessionId) return modal.alert("Select a session and enter a number", "warning");
    setSingleLoading(true);
    setSingleResult(null);
    try {
      const res = await api.post(`/api/verify/${sessionId}/verify-single`, {
        number: singleNumber,
      });
      setSingleResult(res.data);
      // New row always lands on page 1 (desc order). Reset eagerly so the pager
      // UI doesn't show a stale "Page 3" while the fetch is in flight.
      setHistoryPage(1);
      fetchHistory(sessionId, 1);
    } catch {
      modal.alert("Verification failed", "error");
    } finally {
      setSingleLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !sessionId) return modal.alert("Select a session and upload a file", "warning");
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const res = await api.post(`/api/verify/${sessionId}/bulk-verify`, formData);
      setResults(res.data.results);
      setHistoryPage(1);
      fetchHistory(sessionId, 1);
    } catch {
      modal.alert("Verification failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    const ok = await modal.confirm("Clear all verification history for this session?", { danger: true, confirmText: "Clear" });
    if (!ok) return;
    try {
      await api.delete("/api/verify/clear", { params: { session: sessionId } });
      setHistory([]);
      setHistoryTotal(0);
      setHistoryPages(1);
      setHistoryPage(1);
    } catch {
      modal.alert("Failed to clear history", "error");
    }
  };

  const exportToExcel = async () => {
    // Fetch the full session history from the server (uncapped, session-scoped)
    // instead of rebuilding XLSX from the in-memory `results` of the last bulk
    // run — which only ever held the rows from the most recent verify, not the
    // history the user is looking at.
    if (!sessionId) return;
    try {
      const res = await api.get("/api/verify/export", {
        params: { session: sessionId, format: "xlsx" },
        responseType: "blob",
      });
      saveAs(new Blob([res.data]), "whatsapp-verification.xlsx");
    } catch {
      modal.alert("Failed to export verification history", "error");
    }
  };

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "valid" || s === "registered" || s === "exists" || s === "true") {
      return <span className="badge-success flex items-center gap-1"><FaCheckCircle size={10} /> Valid</span>;
    }
    return <span className="badge-danger flex items-center gap-1"><FaTimesCircle size={10} /> Invalid</span>;
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Number Verification"
        subtitle="Check if phone numbers are registered on WhatsApp"
        guideUrl="/guide/number-verification"
      />

      {/* Session Selector */}
      <Card className="mb-5">
        <CardBody>
          <InputGroup label="WhatsApp Session">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-ink-muted text-sm py-2">
                <Spinner size="sm" /> Loading sessions...
              </div>
            ) : sessions.length > 0 ? (
              <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                {sessions.map((s) => (
                  <option key={s.sessionId} value={s.sessionId}>
                    {s.phone || s.sessionId} — {s.name || "WhatsApp Session"}
                  </option>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink-muted py-2">
                <FaWhatsapp className="text-ink-faint" />
                No connected sessions. Connect one from WhatsApp Phones.
              </div>
            )}
          </InputGroup>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Single Verify */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaSearch size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Single Number</span>
            </div>
          </CardHeader>
          <CardBody>
            <InputGroup label="Phone Number">
              <Input
                placeholder="e.g. 919876543210"
                value={singleNumber}
                onChange={(e) => setSingleNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSingleVerify()}
              />
            </InputGroup>
            <Button
              variant="primary"
              className="w-full mt-3"
              onClick={handleSingleVerify}
              loading={singleLoading}
              disabled={!sessionId}
            >
              Verify Number
            </Button>

            {singleResult && (
              <div className="mt-4 p-3 rounded-lg bg-surface-bg border border-surface-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-primary font-medium">
                    {singleResult.number || singleNumber}
                  </span>
                  {statusBadge(singleResult.status)}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Bulk Verify */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaUpload size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Bulk Verify</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => downloadSampleCsv("numbers")}
                className="text-xs text-brand hover:text-brand-700 font-medium transition-colors"
              >
                Download sample CSV
              </button>
              <button
                onClick={() => setShowGuide(true)}
                className="flex items-center gap-1.5 text-xs text-brand hover:text-brand-700 font-medium transition-colors"
              >
                <FaInfoCircle size={12} />
                Format Guide
              </button>
            </div>
          </CardHeader>
          <CardBody>
            <InputGroup label="Upload File">
              <label className="flex items-center justify-center w-full px-4 py-3 rounded-lg border-2 border-dashed border-surface-border hover:border-brand/40 bg-surface-bg cursor-pointer transition-colors">
                <div className="flex flex-col items-center gap-1 text-center">
                  <FaFileExcel size={18} className="text-ink-faint" />
                  <span className="text-xs text-ink-muted">
                    {file ? file.name : "Click to select .xlsx or .csv file"}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
            </InputGroup>
            <Button
              variant="primary"
              className="w-full mt-3"
              onClick={handleUpload}
              loading={loading}
              disabled={!sessionId}
            >
              Verify Numbers
            </Button>
          </CardBody>
        </Card>
      </div>

      {/* Verification History */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaHistory size={13} className="text-brand" />
            <span className="font-semibold text-ink-primary text-sm">
              Verification History {historyTotal > 0 && `(${historyTotal})`}
            </span>
          </div>
          {historyTotal > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={exportToExcel}>
                <FaFileExcel size={12} className="mr-1.5" />
                Export
              </Button>
              <Button variant="danger" size="sm" onClick={clearHistory}>
                <FaTrash size={10} className="mr-1.5" />
                Clear
              </Button>
            </div>
          )}
        </CardHeader>
        {historyLoading ? (
          <CardBody>
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          </CardBody>
        ) : history.length > 0 ? (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Number</Th>
                    <Th>Status</Th>
                    <Th>Verified At</Th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => (
                    <tr key={r._id || r.id || i}>
                      <Td className="text-ink-faint text-xs">
                        {(historyPage - 1) * HISTORY_PAGE_SIZE + i + 1}
                      </Td>
                      <Td className="font-medium">{r.number}</Td>
                      <Td>{statusBadge(r.status)}</Td>
                      <Td className="text-xs text-ink-muted">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
            {historyPages > 1 && (
              <CardBody>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ink-muted">
                    Page {historyPage} of {historyPages} · {historyTotal} total
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={historyPage <= 1}
                      onClick={() => fetchHistory(sessionId, historyPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={historyPage >= historyPages}
                      onClick={() => fetchHistory(sessionId, historyPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardBody>
            )}
          </>
        ) : (
          <CardBody>
            <EmptyState
              icon={FaSearch}
              title="No verification history"
              description="Verify a single number or upload a file to get started."
            />
          </CardBody>
        )}
      </Card>

      {/* Format Guidelines Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => setShowGuide(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-surface-card rounded-xl shadow-2xl border border-surface-border w-full max-w-lg animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-surface-border">
              <h3 className="text-base font-semibold text-ink-primary">File Format Guidelines</h3>
              <button onClick={() => setShowGuide(false)} className="btn-icon">
                <FaTimes size={14} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Supported formats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Supported File Formats</h4>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-ink-primary">
                      <FaCheckCircle size={11} className="text-green-500" /> CSV files (.csv)
                    </div>
                    <div className="flex items-center gap-2 text-sm text-ink-primary">
                      <FaCheckCircle size={11} className="text-green-500" /> Excel files (.xlsx, .xls)
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Column Requirements</h4>
                  <p className="text-xs text-ink-muted mb-2">First column should contain numbers. Accepted column names:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {["phone", "number", "mobile", "whatsapp", "contact"].map((col) => (
                      <code key={col} className="text-xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono">{col}</code>
                    ))}
                  </div>
                </div>
              </div>

              {/* Phone number format */}
              <div>
                <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Phone Number Format</h4>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2 text-sm text-ink-secondary">
                    <FaInfoCircle size={11} className="text-brand mt-0.5 flex-shrink-0" />
                    Include country code (e.g., 919876543210)
                  </div>
                  <div className="flex items-start gap-2 text-sm text-ink-secondary">
                    <FaInfoCircle size={11} className="text-brand mt-0.5 flex-shrink-0" />
                    Can include + symbol (e.g., +919876543210)
                  </div>
                  <div className="flex items-start gap-2 text-sm text-ink-secondary">
                    <FaInfoCircle size={11} className="text-brand mt-0.5 flex-shrink-0" />
                    Spaces, dashes, and brackets are automatically removed
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="alert-warning flex items-start gap-2">
                <FaInfoCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span className="text-xs">Duplicate numbers are automatically removed. Only unique numbers will be verified.</span>
              </div>

              {/* Examples */}
              <div>
                <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Example Formats</h4>
                <div className="rounded-lg border border-surface-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface-bg">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted">Format</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["919876543210", "Indian number with country code"],
                        ["+1234567890", "US number with + symbol"],
                        ["44 20 1234 5678", "UK number with spaces"],
                        ["(555) 123-4567", "Number with brackets and dashes"],
                      ].map(([fmt, desc]) => (
                        <tr key={fmt} className="border-t border-surface-border">
                          <td className="px-3 py-2">
                            <code className="text-xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono">{fmt}</code>
                          </td>
                          <td className="px-3 py-2 text-xs text-ink-secondary">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-surface-border flex justify-end">
              <Button variant="secondary" size="sm" onClick={() => setShowGuide(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
